import { memo, useEffect, useState } from "react";

import TreeView from "@hl/base-components/lib/TreeView";
import {
  AnimatedMedia,
  ArrowUpRight,
  CheckGreen,
  File as FileIcon,
  Folder,
  Image as ImageIcon,
  Upload as UploadIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { ColorSchemeMode } from "@hl/base-components/lib/theme/ColorSchemeContext";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import {
  UploadStatus,
  useUploadManager,
} from "@hl/base-components/lib/upload/context";
import {
  _CollectionType,
  TransactionType,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import {
  TransactionStateType,
  useTransactionsDispatch,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { FileWithPath } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { Prism } from "@mantine/prism";
import { IconAlertTriangle, IconDownload } from "@tabler/icons";
import { BlobReader, Entry, ZipReader } from "@zip.js/zip.js";
import { isEmpty } from "lodash";
import { ErrorCode, FileError, FileRejection } from "react-dropzone";
import { STATUS_COLOR } from "~/../../base-components/lib/theme/colors";
import { TransactionArgFields } from "~/../../shared-features/lib/features/evm-tx";
import { logError } from "~/../../shared-features/lib/services/logger";

import { IN_DEVELOPMENT, SUPPORT_URL, TOOLS_CDN_URL } from "config";
import { useAuth } from "~/features/auth/context";
import SeriesMetadataTablePreview from "~/features/collections/forms/CreateCollectionMultistepForm/SeriesMetadataTablePreview";
import ZipUpload from "~/features/collections/forms/CreateCollectionMultistepForm/ZipUpload";
import { hlTheme } from "~/features/collections/forms/CreateCollectionMultistepForm/codeTheme";
import {
  SeriesToken,
  SeriesTokenMetadata,
} from "~/features/collections/forms/CreateCollectionMultistepForm/context";

import {
  MetadataEntry,
  parseMetadataFile,
} from "../../../../../collections/forms/CreateCollectionMultistepForm/UploadCollectionAndMetadata";
import { useUpdateCollectionMetadataMutation } from "../../queries.graphql.generated";

const metadataExampleJson = `
[
  {
    "metadataId": "2",
    "background": "New trait value"
  },
  {
    "metadataId": "7",
    "background": "is now... purple!"
  }
]
`;

const imageNode = (label: string, animated: boolean) => ({
  key: label,
  label: `${label}.${animated ? "mp4" : "png"}`,
  icon: animated ? <AnimatedMedia /> : <ImageIcon />,
});

const treeData = [
  {
    key: "metadata.json",
    label: "metadata.json",
    icon: <FileIcon />,
  },
  {
    key: "images",
    label: "images",
    icon: <Folder />,
    children: [...["2", "7"].map((label) => ({ ...imageNode(label, false) }))],
  },
  {
    key: "animations (optional)",
    label: "animations (optional)",
    icon: <Folder />,
    children: [...["2", "7"].map((label) => ({ ...imageNode(label, true) }))],
  },
];

export interface EditSeriesMetadataModalProps {
  collectionId: string;
  onClose: () => void;
  chainId: number;
  hasPendingUpdate: boolean;
  metadataIsDecentralized: boolean;
  reveal: boolean;
  takenIds?: number[];
  collectionSize: number;
  collectionType: _CollectionType;
}

export interface EditSeriesMetadataFormValues {
  mediaUrl: string;
  unzipped: boolean;
}

const MAX_SIZE_MB = 3500;
const MAX_SIZE_GB = MAX_SIZE_MB / 1000;

const EditSeriesMetadataModal = ({
  onClose,
  hasPendingUpdate,
  collectionId,
  metadataIsDecentralized,
  reveal,
  takenIds,
  collectionSize,
  collectionType,
}: EditSeriesMetadataModalProps) => {
  const { walletAddress } = useAuth();
  const getNewTxnId = () => `txn-${Date.now()}`;
  const [error, setError] = useState<string>("");
  const [txnId, setTxnId] = useState<string>(getNewTxnId());

  const transactionDispatch = useTransactionsDispatch();
  const transactionState = useTransactionState(txnId);

  const [page, setPage] = useState<"start" | "preview" | "sign">(
    hasPendingUpdate ? "sign" : "start"
  );
  const form = useForm<EditSeriesMetadataFormValues>({
    initialValues: {
      mediaUrl: "",
      unzipped: false,
    },
    validate: (values) => {
      return {
        mediaUrl: !values.mediaUrl.trim() ? "Media is required" : null,
      };
    },
  });
  const [requiresTx, setRequiresTx] = useState<boolean>(false);
  const [showUpdatesMintedToken, setShowUpdatesMintedToken] =
    useState<boolean>(false);
  const [transactionArgs, setTransactionArgs] =
    useState<TransactionArgFields | null>(null);

  const [updateCollectionMetadata, { reset: updateCollectionMetadataReset }] =
    useUpdateCollectionMetadataMutation({
      onCompleted(data) {
        if (data && walletAddress) {
          setTransactionArgs(data.updateCollectionMetadata);
        }
      },
      onError(err) {
        logError({
          err,
          message: "failed to update a collection's metadata",
        });
        setError(err.message);
        updateCollectionMetadataReset();
      },
    });

  // ui
  // show message warning it's not paused, if not paused and its reveal based
  // show copy warning it cannot go with current ongoing tx
  // if requires tx, add message below button
  // if showUpdatesMintedToken, show red alert message above

  /*
  // useCollectionPendingSeriesMetadataUpdateQuery();

  const {
    dispatch,
    state: {
      series: { uploadedTokens },
    },
  } = useCreateCollectionState();

  const tokens = useMemo(
    () => (newTokens.length ? newTokens : uploadedTokens || []),
    [newTokens, uploadedTokens]
  );
  */
  const { state, dispatch: uploadDispatch } = useUploadManager();
  const [tokens, setTokens] = useState<SeriesToken[]>([]);

  const onMediaChanged = (url: string | null) => {
    form.setFieldValue("mediaUrl", url ?? "");
    /*
    dispatch({
      type: "UPDATE_SERIES",
      payload: {
        newMediaUrl: url,
      },
    });
    */
  };

  useEffect(() => {
    if (page === "sign") {
      updateCollectionMetadata({
        variables: {
          collectionId,
          data: {
            seriesDiffUrl: form.values.mediaUrl,
          },
        },
      });
    }
  }, [page]);

  let buttonLabel = !!transactionArgs
    ? "Update metadata"
    : "Uploading to Arweave...";
  let buttonDisabled = false;
  if (transactionState && !transactionState.error && !!transactionState.args) {
    if (transactionState.type === TransactionStateType.WaitingSignedTx) {
      buttonLabel = "Waiting for user signature...";
      buttonDisabled = true;
    } else if (transactionState.type !== TransactionStateType.Done) {
      buttonLabel = "Updating ...";
      buttonDisabled = true;
    }
  }

  /*
  useEffect(() => {
    if (lifecycle?.state === "DONE") {
      onClose();
    }
  }, [lifecycle?.state]);
  */

  const onSubmit = async () => {
    if (
      !transactionArgs ||
      !transactionArgs.address ||
      !transactionArgs.method ||
      !transactionArgs.value
    ) {
      return;
    }

    setError("");

    if (!collectionId) {
      throw new Error("Collection without id");
    }

    try {
      if (walletAddress) {
        const txnId = getNewTxnId();

        transactionDispatch?.({
          type: "START_TRANSACTION",
          payload: {
            id: txnId,
            entityId: collectionId,
            transactionType: TransactionType.EVM_UPDATE_METADATA,
            collectionType,
            args: transactionArgs,
            metadata: {
              collectionId,
              nftContractAddress: walletAddress,
              txType: TransactionType.EVM_UPDATE_METADATA,
            },
          },
        });
        setTxnId(txnId);
      }
    } catch (e) {
      setError("Failed to save changes.");
      logError({
        e,
        message: "[EditSeriesMetadataModal] Failed to update",
      });
    }
  };

  // partial update validation
  const zipFileValidation = async (
    files: FileWithPath[]
  ): Promise<FileRejection[]> => {
    // ZipUpload "multiple" option is false by default
    const file = files[0];

    const errors: FileError[] = [];
    let hasMetadataJson = false;
    let metadataFileName;
    let hasBadTokenFilenameError = false;
    let hasMissingMetadataIdError = false;
    let hasInvalidImageRefError = false;
    let hasInvalidAnimationRefError = false;
    let tokenImageCount = 0;
    let metadata: MetadataEntry[] = [];
    const images: { [name: string]: Entry } = {};
    const animations: { [name: string]: Entry } = {};
    const usedImages: { [name: string]: boolean } = {};
    const usedAnimations: { [name: string]: boolean } = {};
    const modifiedIds: { [id: string]: SeriesTokenMetadata } = {};

    const validFilename =
      /^(animations|images)\/[\w\.\-]+.(png|jpeg|jpg|gif|webp|mp4|webm)/;
    const validInnerFileName = /[\w\.\-]+.(png|jpeg|jpg|gif|webp|mp4|webm)/;

    if (file.size > MAX_SIZE_MB * 1000000) {
      errors.push({
        code: ErrorCode.FileTooLarge,
        message: `zip file cannot be greater than ${MAX_SIZE_GB}GB.`,
      });
    }

    const zipReader = new ZipReader(new BlobReader(file));
    const entries = (await zipReader.getEntries()).filter(
      (entry) => !entry.filename.match(/^__MACOSX\/|\.DS_Store/)
    );

    // TODO: validate that metadataIds are strictly increasing from 1

    /*
     * expect format to be:
     *   - metadata.json
     *   - images/image1.png
     *   - images/image2.png
     *   ...
     *   - animations/animation1.mp4
     *   - animations/animation2.mp4
     */
    for (const entry of entries) {
      if (
        entry.directory &&
        entry.filename !== "images/" &&
        entry.filename !== "animations/"
      ) {
        errors.push({
          code: ErrorCode.FileInvalidType,
          message: `zip file has an unexpected folder: ${entry.filename}`,
        });
      } else if (
        !entry.directory &&
        ["metadata.json", "metadata.csv"].includes(entry.filename)
      ) {
        if (hasMetadataJson) {
          console.log("two metadata files");
          errors.unshift({
            code: ErrorCode.FileInvalidType,
            message: `zip file has multiple metadata files: ${metadataFileName} and ${entry.filename}`,
          });
        }
        hasMetadataJson = true;
        metadataFileName = entry.filename;
        metadata = (await parseMetadataFile(entry, errors)) ?? [];
      } else if (!entry.directory) {
        const validName = entry.filename.match(validFilename);
        // in tokens dir
        if (!validName) {
          if (!hasBadTokenFilenameError) {
            hasBadTokenFilenameError = true;
            errors.push({
              code: ErrorCode.FileInvalidType,
              message: `bad token name(s) - expect series of filenames 1, 2, 3, ..., 100.png (or valid file-extension)`,
            });
          }
          errors.push({
            code: ErrorCode.FileInvalidType,
            message: `zip file has token image with bad name: ${entry.filename}`,
          });
        } else {
          tokenImageCount += 1;

          const fileNameParts = entry.filename.split("/");
          const fileName = fileNameParts[1];
          if (fileNameParts[0] === "images") {
            images[fileName] = entry;
          } else if (fileNameParts[0] === "animations") {
            animations[fileName] = entry;
          }
        }
      }
    }

    await zipReader.close();
    if (!hasMetadataJson) {
      errors.unshift({
        code: ErrorCode.FileInvalidType,
        message: "zip file does not have a metadata.json or metadata.csv file",
      });
    }

    let entry = 0;
    for (const diffMetadata of metadata) {
      entry += 1;
      if (!diffMetadata.metadataId) {
        if (!hasMissingMetadataIdError) {
          hasMissingMetadataIdError = true;
          errors.unshift({
            code: ErrorCode.FileInvalidType,
            message: `Missing metadataId in entry ${entry} in ${metadataFileName} file`,
          });
        }
      } else {
        modifiedIds[diffMetadata.metadataId] = diffMetadata;

        if (
          diffMetadata.metadataId > collectionSize ||
          diffMetadata.metadataId < 1
        ) {
          errors.unshift({
            code: ErrorCode.FileInvalidType,
            message: `Metadata id ${diffMetadata.metadataId} does not correspond to a valid metadata in the series`,
          });
        }

        if (diffMetadata.imageRef) {
          const validInnerName =
            diffMetadata.imageRef.match(validInnerFileName);
          if (!validInnerName) {
            if (!hasInvalidImageRefError) {
              hasInvalidImageRefError = true;
              errors.unshift({
                code: ErrorCode.FileInvalidType,
                message: `Invalid reference name to image in entry ${entry} in ${metadataFileName} file`,
              });
            }
          } else {
            if (!(diffMetadata.imageRef in images)) {
              if (!hasInvalidImageRefError) {
                hasInvalidImageRefError = true;
                errors.unshift({
                  code: ErrorCode.FileInvalidType,
                  message: `Could not find reference image from entry ${entry} in ${metadataFileName} file`,
                });
              }
            } else {
              modifiedIds[diffMetadata.metadataId].imageEntry =
                images[diffMetadata.imageRef];
              usedImages[diffMetadata.imageRef] = true;
            }
          }
        }

        if (diffMetadata.animationRef) {
          const validInnerName =
            diffMetadata.animationRef.match(validInnerFileName);
          if (!validInnerName) {
            if (!hasInvalidAnimationRefError) {
              hasInvalidAnimationRefError = true;
              errors.unshift({
                code: ErrorCode.FileInvalidType,
                message: `Invalid reference name to animation in entry ${entry} in ${metadataFileName} file`,
              });
            }
          } else {
            if (!(diffMetadata.animationRef in animations)) {
              if (!hasInvalidAnimationRefError) {
                hasInvalidAnimationRefError = true;
                errors.unshift({
                  code: ErrorCode.FileInvalidType,
                  message: `Could not find reference animation from entry ${entry} in ${metadataFileName} file`,
                });
              }
            } else {
              modifiedIds[diffMetadata.metadataId].animationEntry =
                animations[diffMetadata.animationRef];
              usedAnimations[diffMetadata.animationRef] = true;
            }
          }
        }
      }
    }

    if (Object.keys(modifiedIds).length > collectionSize) {
      errors.unshift({
        code: ErrorCode.TooManyFiles,
        message: `Number of metadatas updated is greater than series size`,
      });
    }

    if (Object.keys(images).length != Object.keys(usedImages).length) {
      if (!hasMissingMetadataIdError) {
        errors.unshift({
          code: ErrorCode.TooManyFiles,
          message: `Unused image`,
        });
      }
    }

    if (Object.keys(animations).length != Object.keys(usedAnimations).length) {
      if (!hasMissingMetadataIdError) {
        errors.unshift({
          code: ErrorCode.TooManyFiles,
          message: `Unused animation`,
        });
      }
    }

    let idTaken = false;
    if (!!takenIds) {
      for (const takenId of takenIds) {
        if (takenId in modifiedIds) {
          idTaken = true;
        }
      }
    }
    setShowUpdatesMintedToken(idTaken);

    let requiresTxTemp = false;
    if (
      (reveal && metadataIsDecentralized && idTaken) ||
      (!reveal && metadataIsDecentralized)
    ) {
      requiresTxTemp = true;
    }
    setRequiresTx(requiresTxTemp);

    setTokens(Object.values(modifiedIds) ?? []);

    IN_DEVELOPMENT &&
      console.log("validate zip file", {
        errors,
        hasMetadataJson,
        tokenImageCount,
        metadata,
      });
    if (isEmpty(errors)) {
      /*
      dispatch({
        type: "UPDATE_SERIES",
        payload: {
          newTokenImageCount: tokenImageCount,
        },
      });
      */
      return [];
    } else {
      return [
        {
          file: files[0],
          errors,
        },
      ];
    }
  };

  const handleEntries = async (entries: Entry[]) => {
    console.log(entries);
    /*
    let metadata: SeriesTokenMetadata[] | undefined = undefined;
    const imageMap = new Map<number, Entry>();
    for (const entry of entries) {
      if (entry.filename.endsWith(".json")) {
        metadata = JSON.parse(await entry.getData(new TextWriter()));
      } else {
        const paths = entry.filename.split("/");
        imageMap.set(+paths[paths.length - 1].split(".")[0], entry);
      }
    }

    const parsedTokens = metadata?.map((token, index) => ({
      ...token,
      imageEntry: imageMap.get(index + 1),
    }));

    console.log(parsedTokens)

    //setTokens(parsedTokens ?? []);
    */
  };

  const showExampleData = false;

  return (
    <Modal
      opened
      title={
        <Text weight={500} size="lg">
          Update series metadata
        </Text>
      }
      onClose={onClose}
      size="xl"
    >
      {page == "start" && (
        <Stack spacing={16}>
          <ZipUpload
            validate={zipFileValidation}
            handleEntries={handleEntries}
            mediaError={(form.errors["mediaUrl"] as string) ?? ""}
            onMediaChanged={onMediaChanged}
          />
          <Box>
            <Text size={25} weight={500}>
              Instructions
            </Text>
            <Grid mt={13}>
              <Grid.Col span={6} pr={6}>
                <Anchor
                  c="button"
                  type="button"
                  href={`${TOOLS_CDN_URL}/files/highlight-diff-example.zip`}
                  download={"highlight-diff-example.zip"}
                >
                  <Button
                    size="lg"
                    px={16}
                    color={SECONDARY_COLOR}
                    leftIcon={<IconDownload />}
                    fullWidth
                  >
                    Download example .zip
                  </Button>
                </Anchor>
              </Grid.Col>
              <Grid.Col span={6} pl={6}>
                <Anchor
                  c="button"
                  target="_blank"
                  rel="noreferrer"
                  href={SUPPORT_URL.SERIES_LEARN_MORE}
                  type="button"
                >
                  <Button
                    size="lg"
                    pl={16}
                    pr={10}
                    color={SECONDARY_COLOR}
                    leftIcon={<FileIcon />}
                    rightIcon={<ArrowUpRight width={80} height={80} />}
                    fullWidth
                  >
                    Read the full docs
                  </Button>
                </Anchor>
              </Grid.Col>
            </Grid>
            <Text size="sm" component="p" mt={20}>
              To update your collection&apos;s metadata, upload a zip with the
              same format you used when creating the collection. The .zip file
              should be 3.5GB or less. However you only need to include assets
              and metadata you want to update. Any assets or metadata you
              don&apos;t include in this .zip will remain unchanged.
            </Text>
            <Text size="sm" component="p" mt={20}>
              For example, to change an image, you would upload a new
              &quot;images&quot; folder containing the new image file. Then, in
              the metadata.json file, you would update the &quot;imageRef&quot;
              entry for any tokens you want to use this new image.
            </Text>
          </Box>
          {showExampleData && (
            <Group grow align="stretch">
              <Stack spacing={8} align="stretch">
                <Text size="sm" weight={500}>
                  Example folder structure
                </Text>
                <Card
                  sx={(theme) => ({
                    flex: 1,
                    backgroundColor:
                      theme.colorScheme === ColorSchemeMode.DARK
                        ? theme.colors.primaryBackground[0]
                        : theme.colors.tableRowBackground[0],
                  })}
                >
                  <TreeView treeData={treeData} />
                </Card>
              </Stack>
              <Stack spacing={8} align="stretch">
                <Text size="sm" weight={500}>
                  Example metadata.json file
                </Text>
                <Prism
                  getPrismTheme={(theme) => hlTheme(theme)}
                  language="javascript"
                  withLineNumbers
                >
                  {metadataExampleJson}
                </Prism>
              </Stack>
            </Group>
          )}
          <Divider mx={-16} />
          <Group grow>
            {state.status !== UploadStatus.NOT_STARTED && (
              <Button
                size="xl"
                color={SECONDARY_COLOR}
                onClick={() => {
                  uploadDispatch({
                    type: "UPLOAD_RESET",
                  });
                }}
              >
                <Group spacing={8}>
                  <UploadIcon width={16} /> Upload new .zip
                </Group>
              </Button>
            )}
            <Button
              size="xl"
              disabled={[UploadStatus.NOT_STARTED, UploadStatus.ERROR].includes(
                state.status
              )}
              onClick={() => setPage("preview")}
            >
              Review updates
            </Button>
          </Group>
        </Stack>
      )}
      {page == "preview" && (
        <Stack spacing={16}>
          <Box>
            <Text size="sm" component="p">
              Review your updated metadata to ensure it is correct. When you’re
              ready, click the Upload button to upload the updated metadata to
              Arweave.{" "}
              {requiresTx
                ? "This operation will require a transaction to complete the upload."
                : "All tokens in this collection will update as soon as the upload is complete."}
            </Text>
            <Text size="sm" component="p">
              Note that only tokens that have been updated appear here. Updating
              this collection&apos;s metadata will pause minting for this
              collection. Minting will automatically restart as soon as the
              update is complete. You can manually restart the mint by selecting
              &quot;Edit mint&quot; on the collection management page and then
              clicking &quot;Restart&quot;, but we don&apos;t recommend doing so
              unless the update has failed.
            </Text>
          </Box>
          {showUpdatesMintedToken && (
            <Alert
              icon={<IconAlertTriangle color="red" />}
              color="red"
              variant="light"
              styles={{ body: { borderColor: "red", borderWidth: "10px" } }}
            >
              You&apos;re about to update at least one token that has already
              been minted.
            </Alert>
          )}
          <SeriesMetadataTablePreview tokens={tokens} onUpdate={true} />
          <Divider mx={-16} />
          <Group grow>
            <Button
              size="xl"
              color={SECONDARY_COLOR}
              disabled={[UploadStatus.NOT_STARTED, UploadStatus.ERROR].includes(
                state.status
              )}
              onClick={() => {
                setPage("start");
              }}
            >
              Go back
            </Button>
            <Button
              size="xl"
              disabled={[
                UploadStatus.NOT_STARTED,
                UploadStatus.ERROR,
                UploadStatus.IN_PROGRESS,
              ].includes(state.status)}
              onClick={() => setPage("sign")}
            >
              Upload new metadata
            </Button>
          </Group>
        </Stack>
      )}
      {page == "sign" && (
        <Stack spacing={16}>
          <Text size="sm" component="p">
            {requiresTx
              ? "We’re now uploading your updated metadata to Arweave. This could take several minutes depending on the size of your metadata. Once the metadata finishes uploading, click the Update metadata button below to complete the process."
              : "We’re now uploading your updated metadata to Arweave. This could take several minutes depending on the size of your metadata."}
          </Text>

          {(requiresTx &&
            transactionState?.type != TransactionStateType.Done) ||
          (!requiresTx && !transactionArgs) ? (
            <ZipUpload
              validate={zipFileValidation}
              handleEntries={handleEntries}
              mediaError={(form.errors["mediaUrl"] as string) ?? ""}
              onMediaChanged={onMediaChanged}
            />
          ) : (
            <Card p={60}>
              <Stack align="center" spacing={5}>
                <CheckGreen />
                <Text weight="bold">Update complete</Text>
              </Stack>
            </Card>
          )}
          {error && (
            <Text size="sm" color={STATUS_COLOR.ERROR}>
              {error}
            </Text>
          )}
          <Divider mx={-16} />
          <Box>
            <Group grow>
              <Button
                color={SECONDARY_COLOR}
                size="xl"
                disabled={[
                  UploadStatus.NOT_STARTED,
                  UploadStatus.ERROR,
                ].includes(state.status)}
                onClick={() => {
                  uploadDispatch({
                    type: "UPLOAD_RESET",
                  });
                  onClose();
                }}
              >
                Cancel
              </Button>
              {requiresTx && (
                <Button
                  disabled={
                    [UploadStatus.NOT_STARTED, UploadStatus.ERROR].includes(
                      state.status
                    ) ||
                    buttonDisabled ||
                    !transactionArgs
                  }
                  size="xl"
                  onClick={onSubmit}
                >
                  {buttonLabel}
                </Button>
              )}
            </Group>
            {requiresTx && (
              <Text size="xs" align="center" mt={8}>
                This action requires a transaction
              </Text>
            )}
          </Box>
        </Stack>
      )}
    </Modal>
  );
};

export default memo(EditSeriesMetadataModal);
